import React from "react";
import { motion } from "motion/react";
import File from "@/assets/icons/File.svg";
import CopyData from "@/assets/icons/CopyData.svg";
import Download from "@/assets/icons/Download.svg";
import { type Report } from "@/lib/types";
import { showCustomToast } from "../CustomToast";

interface ElementComment {
  url: string;
  selector: string;
  xpath?: string;
  text: string;
  commentNumber: number;
  textContent?: string;
  directText?: string;
  attributes?: Record<string, string>;
  innerHTML?: string;
  outerHTML?: string;
  username?: string;
}

interface ThreeDotsDropdownProps {
  setIsThreeDotsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  report: Report & { elementComments?: ElementComment[] };
}

const ThreeDotsDropdown = ({
  setIsThreeDotsDropdownOpen,
  report,
}: ThreeDotsDropdownProps) => {
  const handleCopyReportData = async () => {
    try {
      // Collect all annotations
      const annotations = [
        // Regular comments
        ...(report.comments || []).map((comment: any) => ({
          text: comment.text,
          user: comment.user?.name || comment.user?.email,
          element: null,
        })),
        // Element comments (annotations on elements)
        ...(report.elementComments || []).map((comment: ElementComment) => ({
          text: comment.text,
          user: comment.username,
          element: {
            selector: comment.selector,
            xpath: comment.xpath,
            textContent: comment.textContent,
            outerHTML: comment.outerHTML,
          },
        })),
        // Screenshot comments
        ...(report.screenshotComments || []).map((comment: any) => ({
          text: comment.text,
          user: comment.username,
          element: null,
        })),
      ];

      const consoleLogs = report.consoleLogs || [];
      const networkErrors =
        report.networkLogs?.filter((log) => log.error || log.status >= 400) ||
        [];

      // Format as markdown
      const formattedText = `# Bug Report: ${report.title || "Untitled"}

## Environment

- **URL:** ${report.info?.url || "N/A"}
- **Timestamp:** ${report.createdAt}
- **Window Size:** ${report.info?.windowSize?.width || "Unknown"}x${report.info?.windowSize?.height || "Unknown"}
- **User Agent:** ${navigator.userAgent}
- **Platform:** ${report.info?.os || "Unknown"}

## Annotations

${
  annotations.length > 0
    ? annotations
        .map((annotation, index) => {
          let output = `${index + 1}. ${annotation.text}`;
          if (annotation.element) {
            const selector =
              annotation.element.selector || annotation.element.xpath;
            if (selector) {
              output += `\n   - **Element:** \`${selector}\``;
            }
            if (annotation.element.textContent) {
              output += `\n   - **Text Content:** ${annotation.element.textContent}`;
            }
          }
          return output;
        })
        .join("\n")
    : "*No annotations*"
}

## Console Logs

${
  consoleLogs.length > 0
    ? consoleLogs
        .map((log, index) => `${index + 1}. \`${log.message}\``)
        .join("\n")
    : "*No console logs*"
}

## Network Errors

${
  networkErrors.length > 0
    ? networkErrors
        .map(
          (error, index) =>
            `${index + 1}. **[${error.status}]** ${error.method} ${error.url}`,
        )
        .join("\n")
    : "*No network errors*"
}`;

      await navigator.clipboard.writeText(formattedText);
      showCustomToast("Report data copied to clipboard", "check");
    } catch (error) {
      console.error("Failed to copy report data:", error);
      showCustomToast("Failed to copy report data");
    }
    setIsThreeDotsDropdownOpen(false);
  };

  const handleDownloadHAR = () => {
    try {
      // Create HAR format from network logs
      const har = {
        log: {
          version: "1.2",
          creator: {
            name: "Toolbar",
            version: "1.0",
          },
          pages: [
            {
              startedDateTime: report.createdAt,
              id: "page_1",
              title: report.title || "Untitled",
              pageTimings: {
                onContentLoad: -1,
                onLoad: -1,
              },
            },
          ],
          entries: (report.networkLogs || []).map((log: any) => {
            const requestHeaders = (log.requestHeaders || []).map((header: any) => ({
              name: header.name,
              value: header.value,
            }));
            const responseHeaders = (log.responseHeaders || []).map((header: any) => ({
              name: header.name,
              value: header.value,
            }));

            // Parse query string params from the URL
            let queryString: { name: string; value: string }[] = [];
            try {
              const urlObj = new URL(log.url);
              urlObj.searchParams.forEach((value, name) => {
                queryString.push({ name, value });
              });
            } catch {
              // If URL parsing fails, leave queryString empty
            }

            // Compute headersSize from headers if the raw size isn't recorded
            const computeHeadersSize = (headers: { name: string; value: string }[]) =>
              headers.reduce((sum, h) => sum + h.name.length + h.value.length + 4, 0); // +4 for ": " and "\r\n"

            return {
              startedDateTime: new Date(log.timestamp).toISOString(),
              time: log.duration || 0,
              request: {
                method: log.method,
                url: log.url,
                httpVersion: "HTTP/1.1",
                headers: requestHeaders,
                queryString,
                cookies: [],
                headersSize: log.requestHeadersSize ?? (requestHeaders.length > 0 ? computeHeadersSize(requestHeaders) : -1),
                bodySize: log.requestBodySize || 0,
                ...(log.requestPayload && {
                  postData: {
                    mimeType: "application/json",
                    text: log.requestPayload,
                  },
                }),
              },
              response: {
                status: log.status,
                statusText: log.statusText || "",
                httpVersion: "HTTP/1.1",
                headers: responseHeaders,
                cookies: [],
                content: {
                  size: log.responseSize || 0,
                  compression: 0,
                  mimeType: log.mimeType || "application/octet-stream",
                  ...(log.responseBody && { text: log.responseBody }),
                },
                redirectURL: "",
                headersSize: log.responseHeadersSize ?? (responseHeaders.length > 0 ? computeHeadersSize(responseHeaders) : -1),
                bodySize: log.responseSize || 0,
              },
              cache: {},
              timings: {
                blocked: -1,
                dns: -1,
                connect: -1,
                ssl: -1,
                send: 0,
                wait: log.duration || 0,
                receive: 0,
              },
              serverIPAddress: log.remoteIPAddress || "",
              pageref: "page_1",
            };
          }),
        },
      };

      // Convert to JSON string
      const harJson = JSON.stringify(har, null, 2);

      // Create blob and download
      const blob = new Blob([harJson], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report.title || "report"}-${report._id}.har`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showCustomToast("HAR file downloaded", "check");
    } catch (error) {
      console.error("Failed to download HAR file:", error);
      showCustomToast("Failed to download HAR file");
    }
    setIsThreeDotsDropdownOpen(false);
  };

  const menuItems = [
    // {
    //   icon: CopyData,
    //   label: "Copy report data",
    //   onClick: handleCopyReportData,
    // },
    {
      icon: File,
      label: "Download HAR file",
      onClick: handleDownloadHAR,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute dropdown-shadow top-[calc(100%+4px)] text-[14px] leading-[20px] right-0 p-[4px] bg-gray-400 w-[240px] rounded-[16px] z-[100]"
    >
      <ul>
        {menuItems.map((item, index) => (
          <li
            key={index}
            onClick={item.onClick}
            className="flex font-medium justify-between hover:bg-gray-500 transition-all rounded-[12px] cursor-pointer items-center h-[36px] pl-[10px] pr-[12px]"
          >
            <div className="flex gap-1.5 items-center text-white">
              <img src={item.icon} alt={item.label} />
              {item.label}
            </div>
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

export default ThreeDotsDropdown;
